import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutHeader as Header, LayoutFooter as Footer, LayoutHero as Hero } from '@/layouts';
import { LayoutRecentlyViewedVehicles as RecentlyViewedVehicles } from '@/layouts';
import { LayoutVehicleCard as VehicleCard } from '@/layouts';
import { HomeBrandsSection } from './HomeBrandsSection';
import { HomeCategoryCard } from './HomeCategoryCard';
import { HomeSectionTitle } from './HomeSectionTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Search, Filter, SlidersHorizontal, MessageCircle, Shield, Car, Wrench, ArrowRight } from 'lucide-react';
import { useAppData, useFilteredVehicles } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { openWhatsApp } from '@/lib/whatsapp';
import { generateVehicleSlug } from '@/lib/slug';
import { useStore } from '@/contexts/StoreContext';
import { Layout5Root } from './Layout5Root';
import './styles.css';
import './home.css';

const Index = () => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const contextVehicles = useFilteredVehicles(selectedStore);
  const [vehicles, setVehicles] = useState(contextVehicles);
  const [filteredVehicles, setFilteredVehicles] = useState(contextVehicles);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedFuel, setSelectedFuel] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 1000000]);
  const [showFilters, setShowFilters] = useState(false);
  const { colors } = useTheme();
  
  // Get selected store
  const store = selectedStore || config.stores[0];

  const buttonColor = colors.button;
  const titleColor = colors.title;
  const textColor = colors.text;

  // Update vehicles when context changes
  useEffect(() => {
    setVehicles(contextVehicles);
    setFilteredVehicles(contextVehicles);
  }, [contextVehicles]);

  // Get unique brands for filter
  const brands = Array.from(new Set(vehicles.map(v => v.brand))).sort();
  const fuels = Array.from(new Set(vehicles.map(v => v.fuel))).sort();

  // Generate autocomplete suggestions
  const suggestions = useMemo(() => {
    if (searchTerm.length < 2) return [];

    const term = searchTerm.toLowerCase();
    const results: Array<{ type: string; text: string; vehicle?: any }> = [];

    // Suggest brands
    brands.forEach(brand => {
      if (brand.toLowerCase().includes(term)) {
        results.push({ type: 'brand', text: brand });
      }
    });

    // Suggest models
    const models = Array.from(new Set(vehicles.map(v => `${v.brand} ${v.model}`)));
    models.forEach(model => {
      if (model.toLowerCase().includes(term) && !results.find(s => s.text === model)) {
        results.push({ type: 'model', text: model });
      }
    });

    // Suggest specific vehicles
    vehicles.forEach(vehicle => {
      const fullText = `${vehicle.brand} ${vehicle.model} ${vehicle.version}`.toLowerCase();
      if (fullText.includes(term) && results.length < 10) {
        const vehicleText = `${vehicle.brand} ${vehicle.model} ${vehicle.version}`;
        if (!results.find(s => s.text === vehicleText)) {
          results.push({
            type: 'vehicle',
            text: vehicleText,
            vehicle: vehicle
          });
        }
      }
    });

    return results.slice(0, 8); // Limit to 8 suggestions
  }, [searchTerm, vehicles, brands]);

  // Filter vehicles based on search and filters
  useEffect(() => {
    let filtered = vehicles.filter(vehicle => {
      const matchesSearch = searchTerm === '' ||
        `${vehicle.brand} ${vehicle.model} ${vehicle.version}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesBrand = selectedBrand === 'all' || vehicle.brand === selectedBrand;
      const matchesFuel = selectedFuel === 'all' || vehicle.fuel === selectedFuel;
      const matchesType = selectedType === 'all' ||
        (selectedType === 'zerokm' && (vehicle.isZeroKm || vehicle.mileage < 100)) ||
        (selectedType === 'seminovo' && !vehicle.isZeroKm && vehicle.mileage >= 100);
      const matchesPrice = vehicle.price >= priceRange[0] && vehicle.price <= priceRange[1];

      return matchesSearch && matchesBrand && matchesFuel && matchesType && matchesPrice;
    });

    setFilteredVehicles(filtered);
  }, [vehicles, searchTerm, selectedBrand, selectedFuel, selectedType, priceRange]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedBrand('all');
    setSelectedFuel('all');
    setSelectedType('all');
    setPriceRange([0, 1000000]);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: any) => {
    if (suggestion.type === 'vehicle' && suggestion.vehicle) {
      // Navigate to vehicle detail with slug
      const slug = generateVehicleSlug(suggestion.vehicle);
      navigate(`/estoque/${slug}`);
    } else {
      // Fill search term and apply filter
      setSearchTerm(suggestion.text);
      setShowSuggestions(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.length >= 2) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
    setSelectedSuggestionIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sortedBrands = useMemo(() => {
    const counts: Record<string, number> = {};
    vehicles.forEach(v => { counts[v.brand] = (counts[v.brand] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [vehicles]);

  const [homeStockTab, setHomeStockTab] = useState<'all' | 'zerokm' | 'seminovo'>('all');

  const showcaseSource = useMemo(() => {
    const featured = vehicles.filter((vehicle) =>
      vehicle.changed?.featured ||
      (vehicle.statistics?.views && vehicle.statistics.views > 100) ||
      (vehicle.statistics?.favorites && vehicle.statistics.favorites > 3)
    );
    return featured.length > 0 ? featured : vehicles;
  }, [vehicles]);

  const usedVehicles = useMemo(
    () => vehicles.filter((v) => !v.isZeroKm && v.mileage > 0 && !v.armored),
    [vehicles]
  );
  const zeroKmVehicles = useMemo(
    () => vehicles.filter((v) => v.isZeroKm || v.mileage === 0),
    [vehicles]
  );

  const showcaseVehicles = useMemo(() => {
    let list = showcaseSource;
    if (homeStockTab === 'zerokm') {
      list = zeroKmVehicles;
    } else if (homeStockTab === 'seminovo') {
      list = usedVehicles;
    }
    return list.slice(0, 3);
  }, [showcaseSource, homeStockTab, zeroKmVehicles, usedVehicles]);
  const armoredVehicles = useMemo(() => vehicles.filter((v) => v.armored), [vehicles]);

  const previewPhoto = (list: typeof vehicles) => list.find((v) => v.photos?.[0])?.photos[0];

  const homeStockTabs = useMemo(
    () => [
      { id: 'all' as const, label: 'Todos', show: true },
      {
        id: 'zerokm' as const,
        label: 'Zero KM',
        show: !!store?.ad?.showZeroKm && zeroKmVehicles.length > 0,
      },
      {
        id: 'seminovo' as const,
        label: 'Seminovos',
        show: !!store?.ad?.showUsed && usedVehicles.length > 0,
      },
    ],
    [store?.ad?.showZeroKm, store?.ad?.showUsed, zeroKmVehicles.length, usedVehicles.length]
  );

  const visibleHomeStockTabs = useMemo(
    () => homeStockTabs.filter((tab) => tab.show),
    [homeStockTabs]
  );

  useEffect(() => {
    if (!homeStockTabs.find((tab) => tab.id === homeStockTab)?.show) {
      setHomeStockTab('all');
    }
  }, [homeStockTab, homeStockTabs]);

  return (
    <Layout5Root withHeader className="wl5-home-page">
      <Header config={config} />
      <Hero config={config} variant="split" />

      {sortedBrands.length > 0 && (
        <HomeBrandsSection
          brands={sortedBrands}
          buttonColor={buttonColor}
          titleColor={titleColor}
        />
      )}

      {showcaseVehicles.length > 0 && (
        <section id="ofertas" className="wl5-av-cars">
          <div className="wl5-container">
            <HomeSectionTitle
              eyebrow="Estoque"
              titleBefore="Veículos em"
              titleAccent="destaque"
              accentColor={buttonColor}
              titleColor={titleColor}
            />

            {visibleHomeStockTabs.length > 1 && (
            <div className="wl5-av-filter-tabs" role="tablist" aria-label="Filtrar veículos">
              {visibleHomeStockTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={homeStockTab === tab.id}
                  className={`wl5-av-filter-tab${homeStockTab === tab.id ? ' is-active' : ''}`}
                  onClick={() => setHomeStockTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            )}

            <div className="wl5-vehicle-grid">
              {showcaseVehicles.map((vehicle) => (
                <VehicleCard
                  key={vehicle.id}
                  vehicle={vehicle}
                />
              ))}
            </div>

            <div className="wl5-av-showcase-more">
              <Link
                to={homeStockTab === 'zerokm' ? '/estoque?type=zerokm' : homeStockTab === 'seminovo' ? '/estoque?type=seminovo' : '/estoque'}
                className="wl5-av-btn wl5-av-btn--solid"
                style={{ backgroundColor: buttonColor }}
              >
                Ver estoque completo
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      <RecentlyViewedVehicles config={config} />

      <section className="wl5-home-shortcuts">
        <div className="wl5-container">
          <HomeSectionTitle
            eyebrow="Categorias"
            titleBefore="Explore por"
            titleAccent="tipo"
            accentColor={buttonColor}
            titleColor={titleColor}
          />
          <div className="wl5-home-shortcuts-grid">
            {store?.ad?.showUsed && usedVehicles.length > 0 && (
              <HomeCategoryCard
                to="/estoque?type=seminovo"
                title="Seminovos"
                description="Veículos revisados com as melhores condições"
                count={usedVehicles.length}
                image={previewPhoto(usedVehicles)}
                icon={Car}
                accentColor={buttonColor}
              />
            )}
            {store?.ad?.showZeroKm && zeroKmVehicles.length > 0 && (
              <HomeCategoryCard
                to="/estoque?type=zerokm"
                title="Zero KM"
                description="Novos, prontos para você"
                count={zeroKmVehicles.length}
                image={previewPhoto(zeroKmVehicles)}
                icon={Car}
                accentColor={buttonColor}
              />
            )}
            {store?.ad?.showArmored && armoredVehicles.length > 0 && (
              <HomeCategoryCard
                to="/estoque?armored=true"
                title="Blindados"
                description="Segurança e proteção para sua família"
                count={armoredVehicles.length}
                image={previewPhoto(armoredVehicles)}
                icon={Shield}
                accentColor={buttonColor}
              />
            )}
            {(store?.defaultServices?.length >= 2 || (store?.services?.length ?? 0) > 0) && (
              <HomeCategoryCard
                to="/servicos"
                title="Serviços"
                description="Financiamento, avaliação e muito mais"
                count={store?.services?.length}
                icon={Wrench}
                accentColor={buttonColor}
              />
            )}
          </div>
        </div>
      </section>

      {/* Old Vehicle Inventory Section - hidden, logic preserved */}
      <section id="estoque-old" className="py-16 bg-background" style={{ display: 'none' }}>
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Nosso Estoque Completo
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Explore nossa seleção completa de veículos zero km e seminovos com a melhor qualidade e preços competitivos
            </p>
          </div>

          {/* Search and Filters */}
          <Card className={`mb-8 shadow-card border-2 transition-all duration-200 ${
            isSearchFocused ? 'border-primary ring-2 ring-primary/20' : 'border-border'
          }`}>
            <CardContent className="p-6">
              {/* Search Bar */}
              <div className="flex flex-col lg:flex-row gap-4 mb-4">
                <div className="flex-1 relative" ref={searchRef}>
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
                  <Input
                    placeholder="Digite marca, modelo, ano ou qualquer palavra-chave..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                      setIsSearchFocused(true);
                      searchTerm.length >= 2 && setShowSuggestions(true);
                    }}
                    onBlur={() => {
                      setIsSearchFocused(false);
                    }}
                    className="pl-10 h-12 text-lg focus-visible:ring-0 focus-visible:ring-offset-0 border-0"
                  />

                  {/* Autocomplete Suggestions */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-lg z-[9999] max-h-[400px] overflow-y-auto">
                      {suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border last:border-b-0 ${index === selectedSuggestionIndex ? 'bg-muted/50' : ''
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                              {suggestion.type === 'brand' && (
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <Search className="h-4 w-4 text-primary" />
                                </div>
                              )}
                              {suggestion.type === 'model' && (
                                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                                  <Car className="h-4 w-4 text-blue-500" />
                                </div>
                              )}
                              {suggestion.type === 'vehicle' && suggestion.vehicle && (
                                <img
                                  src={suggestion.vehicle.photos[0]}
                                  alt={suggestion.text}
                                  className="w-12 h-12 object-cover rounded"
                                />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-foreground truncate">
                                {suggestion.text}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {suggestion.type === 'brand' && 'Marca'}
                                {suggestion.type === 'model' && 'Modelo'}
                                {suggestion.type === 'vehicle' && suggestion.vehicle && (
                                  `${suggestion.vehicle.modelYear} • ${new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0
                                  }).format(suggestion.vehicle.price)}`
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 h-12 px-6"
                >
                  <SlidersHorizontal className="h-5 w-5" />
                  Filtros Avançados
                </Button>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                      <SelectTrigger>
                        <SelectValue placeholder="Marca" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as Marcas</SelectItem>
                        {brands.map(brand => (
                          <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={selectedFuel} onValueChange={setSelectedFuel}>
                      <SelectTrigger>
                        <SelectValue placeholder="Combustível" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {fuels.map(fuel => (
                          <SelectItem key={fuel} value={fuel}>{fuel}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={selectedType} onValueChange={setSelectedType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {store?.ad?.showZeroKm && (
                          <SelectItem value="zerokm">Zero KM</SelectItem>
                        )}
                        {store?.ad?.showUsed && (
                          <SelectItem value="seminovo">Seminovos</SelectItem>
                        )}
                      </SelectContent>
                    </Select>

                    <Button variant="outline" onClick={clearFilters}>
                      Limpar Filtros
                    </Button>
                  </div>
                </div>
              )}

              {/* Results Count */}
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {filteredVehicles.length} veículo{filteredVehicles.length !== 1 ? 's' : ''} encontrado{filteredVehicles.length !== 1 ? 's' : ''}
                </span>
                {(searchTerm || selectedBrand !== 'all' || selectedFuel !== 'all' || selectedType !== 'all') && (
                  <span>Filtros ativos</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Vehicles Grid */}
          {filteredVehicles.length > 0 ? (
            <>
              <div className="wl5-vehicle-grid">
                {filteredVehicles.slice(0, 12).map((vehicle) => (
                  <VehicleCard
                    key={vehicle.id}
                    vehicle={vehicle}
                  />
                ))}
              </div>

              {/* Ver mais botão */}
              {filteredVehicles.length > 12 && (
                <div className="text-center mt-12">
                  <Button
                    size="lg"
                    onClick={() => window.location.href = '/estoque'}
                    className="px-8 py-3 text-white"
                    style={{
                      background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                    }}
                  >
                    Ver Mais no Estoque ({filteredVehicles.length - 12} veículos)
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Nenhum veículo encontrado
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Não encontramos veículos com os filtros selecionados.<br />Tente ajustar sua busca ou entre em contato conosco.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={clearFilters}>
                  Limpar Filtros
                </Button>
                <Button
                  variant="outline"
                  onClick={() => openWhatsApp(store.whatsapp?.ios)}
                >
                  Falar com Vendedor
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* <InstagramReels config={config} /> */}

      <Footer config={config} />
    </Layout5Root>
  );
};

export default Index;
